import { generatePath } from 'react-router';

/**
 * Contains the paths of the application routes
 *
 * @example
 * user: {
 *  path: (username = ':username') => `/${username}`
 *  routes: {
 *    settings: {
 *      path: () => '/settings'
 *    }
 *  }
 * }
 */
export const routesScriptsure = {
  root: { path: () => '/' },
  login: { path: () => '/login' },
  forgotEmail: { path: () => '/forgot-email' },
  forgotPassword: { path: () => '/forgot-password' },
  resetPassword: { path: () => '/reset-password' },
  verifyEmail: {
    path: (userId: string | number = ':userId', signupToken = ':signupToken') => `/v3/signup/verify/${userId}/${signupToken}`,
  },
  idproofingComplete: { path: () => '/complete-idproofing' },
  report: { path: () => '/report-v1' },
  reportsV2: { path: () => '/report' },
  message: {
    path: () => '/message',
    newRequest: {
      path: () => `/message/new-request`,
    },
    cancelPending: {
      path: () => `/message/pending-cancel`,
    },
    error: {
      path: () => `/message/pending-error`,
    },
    approved: {
      path: () => `/message/approved`,
    },
    declined: {
      path: () => `/message/declined`,
    },
    cancel: {
      path: () => `/message/history-cancel`,
    },
    errorReviewed: {
      path: () => `/message/history-error`,
    },
  },
  epaMessage: {
    path: () => '/epa-message',
    paNeeded: {
      path: () => `/epa-message/pa-needed`,
    },
    questions: {
      path: () => `/epa-message/question-set-available`,
    },
    pendingDetermination: {
      path: () => `/epa-message/pending-determination`,
    },
    approved: {
      path: () => `/epa-message/approved`,
    },
    closed: {
      path: () => `/epa-message/closed`,
    },
    denied: {
      path: () => `/epa-message/denied`,
    },
    partiallyDenied: {
      path: () => `/epa-message/partially-denied`,
    },
    cancelled: {
      path: () => `/epa-message/cancelled`,
    },
    error: {
      path: () => '/epa-message/error',
    },
  },
  practices: {
    path: () => '/practice-profile',
    profile: {
      path: (practiceId: string | number = ':practiceId') => `/practice-profile/${practiceId}`,
      settings: { path: (practiceId: string | number = ':practiceId') => `/practice-profile/${practiceId}/settings` },
    },
  },
  profile: {
    path: (userId: string | number = ':userId') => `/profile/${userId}`,
    generatePath: (userId: number | string) => generatePath(`/profile/:userId`, { userId }),
    routes: {
      settings: {
        path: (userId: string | number = ':userId') => `/profile/${userId}/settings`,
        generatePath: (userId: number | string) => generatePath(`settings`, { userId, entity: 'settings' }),
      },
    },
  },
  client: {
    path: (clientId: string | number = ':clientId') => `/client/${clientId}`,
  },
  chart: {
    path: (patientId: string | number = ':patientId') => `/chart/${patientId}`,
    routes: {
      prescriptions: { path: () => '/prescriptions' },
      drugList: { path: () => '/drug-list' },
      demographics: { path: () => '/demographics' },
      allergies: { path: () => '/allergies' },
      pharmacy: { path: () => '/pharmacy' },
      diagnosis: { path: () => '/diagnosis' },
      vitals: { path: () => '/vitals' },
      notes: { path: () => '/notes' },
      education: { path: () => '/education' },
    },
  },
  setting: {
    path: () => '/settings-old',
    routes: {
      locations: { path: () => '/practice/locations' },
      notifications: { path: () => '/practice/notifications' },
      prescriptions: { path: () => '/practice/prescriptions' },
      approval: { path: () => '/practice/epics-approval' },
      advancedSetting: { path: () => '/practice/advanced-practice-setting' },
      userPendingAlerts: { path: () => '/user/pending-prescription-alerts' },
      practicePendingAlerts: { path: () => '/practice/pending-prescription-alerts' },
      restoreDefaults: { path: () => '/practice/restore-practice-defaults' },
      userProfile: { path: () => '/user/profile' },
      electronicOption: { path: () => '/user/electronic-option' },
      epicsLogin: { path: () => '/user/epics-login' },
      prescriptionWorkFlow: { path: () => '/user/prescription-work-flow' },
      checks: { path: () => '/user/checks-alerts' },
      print: { path: () => '/user/print' },
      adherence: { path: () => '/user/adherence' },
      termOsUse: { path: () => '/user/terms-of-use' },
      miscellaneousUser: { path: () => '/user/miscellaneous' },
      prescriptionAlerts: { path: () => '/user/prescription-alerts' },
      restoreUserDefaults: { path: () => '/user/restore-user-defaults' },
      userList: { path: () => '/manage/manage-user-list' },
      addUser: { path: () => '/manage/add-user' },
      security: { path: () => '/security' },
      billing: { path: () => '/billing' },
      miscellaneous: { path: () => '/miscellaneous' },
    },
  },
  widgets: {
    path: () => '/widgets',
    routes: {
      allergy: { path: (patientId: string | number = ':patientId') => `/widgets/allergy/${patientId}` },
      diagnosis: { path: (patientId: string | number = ':patientId') => `/widgets/diagnosis/${patientId}` },
      auditLog: { path: () => `/widgets/auditlog` },
      approveQueue: { path: (patientId: string | number = ':patientId') => `/widgets/approve-queue/${patientId}` },
      prescription: {
        path: (patientId: string | number = ':patientId', orderId: string | number = ':orderId') => `/widgets/prescription/${patientId}/${orderId}`,
        confirm: {
          path: (patientId: string | number = ':patientId', orderId: string | number = ':orderId') =>
            `/widgets/prescription/confirmation/${patientId}/${orderId}`,
        },
        favorite: {
          path: (patientId: string | number = ':patientId', formatId: string | number = ':formatId') =>
            `/widgets/prescription/favorite/${patientId}/${formatId}`,
        },
      },
      prescriptionOrderSet: {
        path: (patientId: string | number = ':patientId', orderSetId: string | number = ':orderSetId') =>
          `/widgets/prescription/order-set/${patientId}/${orderSetId}`,
      },
      medcart: { path: (patientId: string | number = ':patientId') => `/widgets/medcart/${patientId}` },
      drugList: { path: (patientId: string | number = ':patientId') => `/widgets/drug-list/${patientId}` },
      prescriptionCompound: {
        path: (patientId: string | number = ':patientId', compoundId: string | number = ':compoundId') =>
          `/widgets/prescription/compound/${patientId}/${compoundId}`,
      },
      drugHistory: { path: (patientId: string | number = ':patientId') => `/widgets/drug-history/${patientId}` },
      pharmacy: { path: (patientId: string | number = ':patientId') => `/widgets/pharmacy/${patientId}` },
      message: { path: (id: string | number = ':id?') => `/widgets/message/${id}` },
      prescriptionQueue: { path: () => `/widgets/prescription-queue` },
      medicationDownload: { path: (patientId: string | number = ':patientId') => `/widgets/medicationdownload/${patientId}` },
      practiceProfile: { path: (practiceId: string | number = ':practiceId') => `/widgets/practice-profile/${practiceId}` },
      practiceSettings: { path: (practiceId: string | number = ':practiceId') => `/widgets/setting/1/${practiceId}` },
      userSettings: { path: (userId: string | number = ':userId') => `/widgets/user/settings/${userId}` },
      mfaSetup: { path: () => `/widgets/mfa/setup` },
      report: { path: () => `/widgets/report` },
    },
  },
  status: { path: () => '/status' },
} as const;
